import type { FC, MouseEventHandler } from 'react';
import { Show } from '@components/Show';
import Tooltip from '@components/Tooltip';
import useDetectedTruncation from '@hooks/useDetectTruncation';
import { cn } from 'cn';
import { truncate } from 'lodash-es';
import { Quote } from 'lucide-react';

interface QuoteChipProps {
  quote: string;
  isRemoved: boolean;
  onClick?: () => void;
  testId?: string;
}

export const QuoteChip: FC<QuoteChipProps> = ({ quote, isRemoved, onClick, testId }) => {
  const { ref, isTruncated } = useDetectedTruncation<HTMLSpanElement>();
  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    onClick?.();
  };

  return (
    <Tooltip content={quote} disabled={!isTruncated} maxWidth={320}>
      <button
        className={cn(
          'flex w-full items-center gap-1 bg-gray-100 px-7 py-1 text-left text-xs leading-5 text-gray-500',
          onClick ? 'hover:bg-info-100 cursor-pointer' : 'cursor-default',
        )}
        data-testid={testId ?? (isRemoved ? 'comment-thread-quote-removed' : 'comment-thread-quote')}
        onClick={handleClick}
        type="button"
      >
        <Quote className="size-3 shrink-0" />
        <span className={cn('min-w-0 truncate', isRemoved && 'line-through')} ref={ref}>
          {truncate(quote, { length: 200 })}
        </span>
        <Show when={isRemoved}>
          <span className="ml-auto shrink-0 pl-2 font-medium text-gray-400">Removed</span>
        </Show>
      </button>
    </Tooltip>
  );
};
